import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Badge,
  Button,
  Alert,
  Input,
  FormGroup
} from 'reactstrap';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const ControlList = () => {
  const { id: sspId } = useParams();
  const navigate = useNavigate();
  const [controls, setControls] = useState([]);
  const [filteredControls, setFilteredControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (sspId) {
      fetchControls();
    }
  }, [sspId]);

  useEffect(() => {
    filterControls();
  }, [controls, searchTerm, filterStatus]);

  const fetchControls = async () => {
    try {
      setLoading(true);
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      const response = await axios.get(`${baseURL}/ssp-studio/controls?sspId=${sspId}`, {
        withCredentials: true
      });
      setControls(response.data.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching controls:', err);
      setError(err.response?.data?.message || err.message);
      setLoading(false);
    }
  };

  const filterControls = () => {
    let filtered = [...controls];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(control =>
        control.control_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        control.control_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(control => control.implementationStatus === filterStatus);
    }

    setFilteredControls(filtered);
  };

  const getStatusBadge = (status) => {
    const colors = {
      implemented: 'success',
      partial: 'warning',
      planned: 'info',
      'not-applicable': 'secondary'
    };
    return <Badge color={colors[status] || 'light'}>{status}</Badge>;
  };

  const getCompletionBadge = (completion) => {
    const colors = {
      empty: 'secondary',
      draft: 'warning',
      ready: 'success'
    };
    return <Badge color={colors[completion] || 'info'}>{completion}</Badge>;
  };

  const getControlFamily = (controlId) => {
    if (!controlId) return 'Unknown';
    const parts = controlId.split('-');
    return parts[0] || 'Unknown';
  };

  const groupByFamily = () => {
    const grouped = {};
    filteredControls.forEach(control => {
      const family = getControlFamily(control.control_id);
      if (!grouped[family]) {
        grouped[family] = [];
      }
      grouped[family].push(control);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading controls...</span>
        </div>
      </div>
    );
  }

  const groupedControls = groupByFamily();
  const implementedCount = controls.filter(c => c.implementationStatus === 'implemented').length;
  const partialCount = controls.filter(c => c.implementationStatus === 'partial').length;
  const readyCount = controls.filter(c => c.narrativeCompletion === 'ready').length;

  return (
    <Container fluid>
      <Row className="mb-3">
        <Col>
          <Button color="secondary" outline size="sm" onClick={() => navigate(`/ssp-studio/ssp/${sspId}`)}>
            <i className="mdi mdi-arrow-left me-1"></i>
            Back to SSP
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Row className="mb-4">
        <Col lg={12}>
          <h4>Security Controls ({controls.length})</h4>
        </Col>
      </Row>

      {/* Statistics Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card>
            <CardBody className="text-center">
              <h3 className="text-primary">{controls.length}</h3>
              <p className="text-muted mb-0">Total Controls</p>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <CardBody className="text-center">
              <h3 className="text-success">{implementedCount}</h3>
              <p className="text-muted mb-0">Implemented</p>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <CardBody className="text-center">
              <h3 className="text-warning">{partialCount}</h3>
              <p className="text-muted mb-0">Partial</p>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <CardBody className="text-center">
              <h3 className="text-info">{readyCount}</h3>
              <p className="text-muted mb-0">Narratives Ready</p>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="mb-3">
        <Col md={6}>
          <FormGroup>
            <Input
              type="text"
              placeholder="Search by control ID or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Input
              type="select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Implementation Statuses</option>
              <option value="implemented">Implemented</option>
              <option value="partial">Partial</option>
              <option value="planned">Planned</option>
              <option value="not-applicable">Not Applicable</option>
            </Input>
          </FormGroup>
        </Col>
      </Row>

      {/* Controls Table - Grouped by Family */}
      {Object.keys(groupedControls).length === 0 ? (
        <Card>
          <CardBody className="text-center py-5">
            <i className="mdi mdi-shield-alert-outline text-muted" style={{ fontSize: '48px' }}></i>
            <h5 className="mt-3">No Controls Found</h5>
            <p className="text-muted">
              {searchTerm || filterStatus !== 'all'
                ? 'No controls match your search criteria'
                : 'This SSP does not have any controls yet'}
            </p>
          </CardBody>
        </Card>
      ) : (
        Object.keys(groupedControls).sort().map(family => (
          <Card key={family} className="mb-3">
            <CardBody>
              <CardTitle className="mb-3">
                <i className="mdi mdi-shield-check text-primary me-2"></i>
                {family} Family ({groupedControls[family].length})
              </CardTitle>
              <Table hover responsive>
                <thead>
                  <tr>
                    <th style={{ width: '15%' }}>Control ID</th>
                    <th style={{ width: '30%' }}>Control Name</th>
                    <th style={{ width: '15%' }}>Implementation</th>
                    <th style={{ width: '15%' }}>Narrative</th>
                    <th style={{ width: '10%' }}>Evidence</th>
                    <th style={{ width: '15%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedControls[family].map((control) => (
                    <tr key={control._id}>
                      <td>
                        <strong>{control.control_id}</strong>
                      </td>
                      <td>{control.control_name || '-'}</td>
                      <td>{getStatusBadge(control.implementationStatus)}</td>
                      <td>{getCompletionBadge(control.narrativeCompletion)}</td>
                      <td>
                        <Badge color="light" className="text-dark">
                          {control.evidenceLinks?.length || 0}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          color="primary"
                          outline
                          onClick={() => navigate(`/ssp-studio/ssp/${sspId}/control/${control._id}`)}
                        >
                          <i className="mdi mdi-pencil"></i> Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        ))
      )}
    </Container>
  );
};

export default ControlList;
